<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { supabase, useSupabase } from "../db/supabaseClient.js";
import { listSales, getSaleById, getSaleItems } from "../db/sales.js";
import { getTaxes } from "../db/inventory.js";
import { generateModernTicket } from "../utils/modernTicketPdf.js";
import Swal from "sweetalert2";
import ChatAssistant from "../components/ChatAssistant.vue";

const loading = ref(false);
const sales = ref([]);
const search = ref("");
const dateFrom = ref("");
const dateTo = ref("");
const paymentFilter = ref("");
const suggestOpen = ref(false);
const suggestLoading = ref(false);
const productSuggestions = ref([]);
const searchWrapRef = ref(null);
const animToday = ref(0);
const animMonth = ref(0);
const animTotalCount = ref(0);
const animAvg = ref(0);
const animTodayAmount = ref(0);
const animMonthAmount = ref(0);
const animAllAmount = ref(0);

function animateNumber(outRef, to, duration = 800) {
  const from = Number(outRef.value || 0);
  const start = performance.now();
  function step(now) {
    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3);
    outRef.value = from + (to - from) * eased;
    if (t < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

// Pagination
const currentPage = ref(1);
const itemsPerPage = 20;

// Detail modal
const showDetailModal = ref(false);
const selectedSale = ref(null);
const saleItems = ref([]);
const loadingDetail = ref(false);
const taxRates = ref(new Map());
const saleStats = ref({}); // saleId -> { discountPct: number, taxPct: number, qtySum: number }

// Reset pagination when filters change
watch([search, dateFrom, dateTo, paymentFilter], () => {
  currentPage.value = 1;
});

onMounted(async () => {
  await initTaxes();
  await loadSales();
  await prefetchPercentsForVisible();
  if (search.value) await loadProductSuggestions(search.value);
  animateNumber(animToday, Number(stats.value.todayCount || 0));
  animateNumber(animMonth, Number(stats.value.monthCount || 0));
  animateNumber(animTotalCount, Number((sales.value || []).length || 0));
  animateNumber(animAvg, Number(stats.value.avgSale || 0));
  animateNumber(animTodayAmount, Number(stats.value.todayTotal || 0));
  animateNumber(animMonthAmount, Number(stats.value.monthTotal || 0));
  animateNumber(animAllAmount, Number(allTotal.value || 0));
});

async function loadSales() {
  loading.value = true;
  try {
    const data = await listSales();
    sales.value = data;
  } finally {
    loading.value = false;
  }
}

const filteredSales = computed(() => {
  let list = sales.value.slice();

  // Sort by ID ascending (1, 2, 3...)
  list.sort((a, b) => a.id - b.id);

  // Search filter
  if (search.value.trim()) {
    const q = search.value.toLowerCase();
    list = list.filter(
      (s) =>
        (s.customer_name || "").toLowerCase().includes(q) ||
        (s.doc_number || "").toLowerCase().includes(q) ||
        (s.payment_method || "").toLowerCase().includes(q) ||
        (s.product_name || "").toLowerCase().includes(q)
    );
  }

  // Date range filter
  if (dateFrom.value) {
    list = list.filter((s) => {
      if (!s.created_at) return false;
      const saleDate = new Date(s.created_at.replace(" ", "T"));
      const saleDateStr =
        saleDate.getFullYear() +
        "-" +
        String(saleDate.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(saleDate.getDate()).padStart(2, "0");
      return saleDateStr >= dateFrom.value;
    });
  }
  if (dateTo.value) {
    list = list.filter((s) => {
      if (!s.created_at) return false;
      const saleDate = new Date(s.created_at.replace(" ", "T"));
      const saleDateStr =
        saleDate.getFullYear() +
        "-" +
        String(saleDate.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(saleDate.getDate()).padStart(2, "0");
      return saleDateStr <= dateTo.value;
    });
  }

  // Payment method filter
  if (paymentFilter.value) {
    list = list.filter(
      (s) =>
        (s.payment_method || "").toLowerCase() ===
        paymentFilter.value.toLowerCase()
    );
  }

  return list;
});

function openSuggestions() {
  suggestOpen.value = true;
  loadProductSuggestions(search.value);
}

function selectSuggestion(p) {
  search.value = p.name || "";
  suggestOpen.value = false;
}

async function loadProductSuggestions(q) {
  const term = String(q || "").trim();
  suggestLoading.value = true;
  try {
    if (useSupabase()) {
      let query = supabase
        .from("v_inventory_basic")
        .select("id,name,sku,image_base64,image_mime")
        .limit(10);
      if (term) query = query.or(`name.ilike.%${term}%,sku.ilike.%${term}%`);
      else query = query.order("name", { ascending: true });
      const { data } = await query;
      productSuggestions.value = (data || []).map(r => ({
        id: r.id,
        name: r.name,
        sku: r.sku || null,
        image_base64: r.image_base64 || null,
        image_mime: r.image_mime || "image/jpeg",
      }));
    } else {
      const names = Array.from(new Set(sales.value.map(s => s.product_name).filter(Boolean)));
      const rows = (term ? names.filter(n => String(n).toLowerCase().includes(term.toLowerCase())) : names).slice(0, 10);
      productSuggestions.value = rows.map(n => ({ id: n, name: n, sku: null, image_base64: null, image_mime: "image/jpeg" }));
    }
  } finally {
    suggestLoading.value = false;
  }
}

watch(search, (val) => {
  const term = String(val || '').trim();
  if (!term) { productSuggestions.value = []; suggestOpen.value = false; return; }
  if (suggestOpen.value) loadProductSuggestions(val);
});

// Paginated sales
const paginatedSales = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredSales.value.slice(start, end);
});

const totalPages = computed(() => {
  return Math.ceil(filteredSales.value.length / itemsPerPage);
});

const paginationRange = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("...");
      pages.push(total);
    } else if (current >= total - 3) {
      pages.push(1);
      pages.push("...");
      for (let i = total - 4; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      for (let i = current - 1; i <= current + 1; i++) pages.push(i);
      pages.push("...");
      pages.push(total);
    }
  }
  return pages;
});

function goToPage(page) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
}

function resetPagination() {
  currentPage.value = 1;
}

// Statistics
const stats = computed(() => {
  const today = new Date().toISOString().split("T")[0];
  const todaySales = sales.value.filter((s) => s.created_at.startsWith(today));

  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const monthSales = sales.value.filter((s) => s.created_at >= firstDayOfMonth);

  return {
    todayCount: todaySales.length,
    todayTotal: todaySales.reduce((sum, s) => sum + s.total, 0),
    monthCount: monthSales.length,
    monthTotal: monthSales.reduce((sum, s) => sum + s.total, 0),
    avgSale:
      sales.value.length > 0
        ? sales.value.reduce((sum, s) => sum + s.total, 0) / sales.value.length
        : 0,
  };
});

const allTotal = computed(() => sales.value.reduce((s, v) => s + v.total, 0));

watch(() => stats.value.todayCount, (nv) => animateNumber(animToday, Number(nv || 0)));
watch(() => stats.value.monthCount, (nv) => animateNumber(animMonth, Number(nv || 0)));
watch(() => sales.value.length, (nv) => animateNumber(animTotalCount, Number(nv || 0)));
watch(() => stats.value.avgSale, (nv) => animateNumber(animAvg, Number(nv || 0)));
watch(() => stats.value.todayTotal, (nv) => animateNumber(animTodayAmount, Number(nv || 0)));
watch(() => stats.value.monthTotal, (nv) => animateNumber(animMonthAmount, Number(nv || 0)));
watch(allTotal, (nv) => animateNumber(animAllAmount, Number(nv || 0)));

// Payment methods for filter
const paymentMethods = computed(() => {
  const methods = new Set(
    sales.value.map((s) => s.payment_method).filter(Boolean)
  );
  return Array.from(methods);
});

async function viewDetail(sale) {
  loadingDetail.value = true;
  showDetailModal.value = true;
  try {
    selectedSale.value = await getSaleById(sale.id);
    saleItems.value = await getSaleItems(sale.id);
    try {
      const taxes = await getTaxes();
      const map = new Map(
        (taxes || []).map((t) => [t.id, Number(t.rate || 0)])
      );
      taxRates.value = map;
    } catch { }
  } finally {
    loadingDetail.value = false;
  }
}

async function initTaxes() {
  try {
    const taxes = await getTaxes();
    const map = new Map((taxes || []).map((t) => [t.id, Number(t.rate || 0)]));
    taxRates.value = map;
  } catch { }
}

function closeDetailModal() {
  showDetailModal.value = false;
  selectedSale.value = null;
  saleItems.value = [];
}

function displayItemDiscount(it) {
  const d = Number(it.discount || 0);
  if (d > 0) return d;
  const perc = Number(it.product_discount_percent || 0);
  if (perc > 0) {
    const unit = Number(it.unit_price || 0);
    const qty = Number(it.quantity || 0);
    return unit * qty * (perc / 100);
  }
  return 0;
}

function displayItemTaxRate(it) {
  const r = Number(it.tax_rate || 0);
  if (r > 0) return r;
  if (it.product_tax_rate != null) return Number(it.product_tax_rate || 0);
  const tid = it.product_tax_id;
  if (tid && taxRates.value && taxRates.value.has(tid))
    return Number(taxRates.value.get(tid) || 0);
  return 0;
}

function displayItemDiscountPercent(it) {
  const perc = Number(it.product_discount_percent || 0);
  if (perc > 0) return Math.min(Math.max(perc, 0), 100);
  const amount = Number(it.discount || 0);
  const base = Number(it.unit_price || 0) * Number(it.quantity || 0);
  if (amount > 0 && base > 0) {
    const derived = (amount / base) * 100;
    return Math.min(Math.max(Math.round(derived), 0), 100);
  }
  return 0;
}

function overallDiscountPercent() {
  const base = saleItems.value.reduce(
    (s, it) => s + Number(it.unit_price || 0) * Number(it.quantity || 0),
    0
  );
  if (base <= 0) return 0;
  const discountAmount = saleItems.value.reduce(
    (s, it) => s + displayItemDiscount(it),
    0
  );
  const pct = (discountAmount / base) * 100;
  return Math.min(Math.max(Math.round(pct), 0), 100);
}

function overallTaxPercent() {
  const rates = saleItems.value
    .map((it) => displayItemTaxRate(it))
    .filter((r) => r > 0);
  if (!rates.length) return 0;
  const set = new Set(rates);
  if (set.size === 1) return rates[0];
  return Math.max(...rates);
}

function discountPercentForSale(s) {
  const sub = Number(s?.subtotal || 0);
  const d = Number(s?.discount_total || 0);
  if (sub > 0 && d > 0)
    return Math.min(Math.max(Math.round((d / sub) * 100), 0), 100);
  const cached = saleStats.value[s?.id];
  return cached?.discountPct ?? 0;
}

function taxPercentForSale(s) {
  const sub = Number(s?.subtotal || 0);
  const t = Number(s?.tax_total || 0);
  if (sub > 0 && t > 0)
    return Math.min(Math.max(Math.round((t / sub) * 100), 0), 100);
  const cached = saleStats.value[s?.id];
  return cached?.taxPct ?? 0;
}

function computeStatsFromItems(items) {
  const base = items.reduce(
    (s, it) => s + Number(it.unit_price || 0) * Number(it.quantity || 0),
    0
  );
  const discountAmount = items.reduce((s, it) => {
    const d = Number(it.discount || 0);
    if (d > 0) return s + d;
    const perc = Number(it.product_discount_percent || 0);
    if (perc > 0)
      return (
        s + Number(it.unit_price || 0) * Number(it.quantity || 0) * (perc / 100)
      );
    return s;
  }, 0);
  const discountPct =
    base > 0
      ? Math.min(Math.max(Math.round((discountAmount / base) * 100), 0), 100)
      : 0;
  const rates = items
    .map((it) => {
      const r = Number(it.tax_rate || 0);
      if (r > 0) return r;
      if (it.product_tax_rate != null) return Number(it.product_tax_rate || 0);
      const tid = it.product_tax_id;
      if (tid && taxRates.value && taxRates.value.has(tid))
        return Number(taxRates.value.get(tid) || 0);
      return 0;
    })
    .filter((r) => r > 0);
  const taxPct = rates.length
    ? new Set(rates).size === 1
      ? rates[0]
      : Math.max(...rates)
    : 0;
  const qtySum = items.reduce((s, it) => s + Number(it.quantity || 0), 0);
  return { discountPct, taxPct, qtySum };
}

async function prefetchPercentsForVisible() {
  const ids = paginatedSales.value.map((s) => s.id);
  const missing = ids.filter((id) => !(id in saleStats.value));
  if (!missing.length) return;
  for (const id of missing) {
    try {
      const items = await getSaleItems(id);
      const p = computeStatsFromItems(items || []);
      saleStats.value = { ...saleStats.value, [id]: p };
    } catch { }
  }
}

watch(paginatedSales, () => {
  prefetchPercentsForVisible();
});

function quantityForSale(s) {
  const cached = saleStats.value[s?.id];
  return cached?.qtySum ?? null;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr.replace(" ", "T"));
    return d.toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

function prettyNotes(raw, method) {
  const text = String(raw || "").trim();
  if (!text) return "";
  const parts = text.split(";").map(s => s.trim()).filter(Boolean);
  const kv = {};
  for (const p of parts) {
    const idx = p.indexOf("=");
    if (idx > 0) {
      const k = p.slice(0, idx).trim();
      const v = p.slice(idx + 1).trim();
      kv[k] = v;
    }
  }
  const pm = String(method || "").toLowerCase();
  if (pm === "cash") {
    const rec = kv.cash_received ? `$${Number(kv.cash_received).toFixed(2)}` : null;
    const chg = kv.change ? `$${Number(kv.change).toFixed(2)}` : null;
    if (rec || chg) return [rec ? `Efectivo recibido: ${rec}` : null, chg ? `Cambio: ${chg}` : null].filter(Boolean).join(" • ");
  }
  if (pm === "card") {
    const last4 = kv.card_last4 ? `•••• ${kv.card_last4}` : null;
    const auth = kv.auth || kv.authorization || null;
    const msg = [last4 ? `Tarjeta: ${last4}` : null, auth ? `Autorización: ${auth}` : null].filter(Boolean).join(" • ");
    if (msg) return msg;
  }
  if (pm === "transfer" || pm === "bank") {
    const ref = kv.reference || kv.bank_ref || null;
    if (ref) return `Referencia bancaria: ${ref}`;
  }
  return text;
}

function money(val) {
  try {
    const n = Number(val || 0);
    return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch {
    const n = Number(val || 0).toFixed(2);
    return '$' + String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

function clearFilters() {
  search.value = "";
  dateFrom.value = "";
  dateTo.value = "";
  paymentFilter.value = "";
  resetPagination();
  suggestOpen.value = false;
  productSuggestions.value = [];
}

async function printTicket(sale) {
  try {
    const fullSale = await getSaleById(sale.id);
    const items = await getSaleItems(sale.id);
    if (!fullSale) {
      alert("No se pudo obtener la información de la venta");
      return;
    }
    await generateModernTicket({
      logoUrl: "/img/sushi.png",
      title: "Sushito",
      saleId: sale.id,
      items: items.map((item) => ({
        name: item.product_name || "Producto",
        qty: item.quantity,
        unit_price: item.unit_price,
        total: item.total,
        image: item.product_image,
        mime: item.product_image_mime,
      })),
      subtotal: sale.subtotal,
      discount: sale.discount_total,
      tax: sale.tax_total,
      total: sale.total,
      paymentMethod: sale.payment_method,
      customerName:
        sale.customer_name !== "Sin cliente" ? sale.customer_name : null,
      date: formatDate(sale.created_at),
    });

    Swal.fire({
      title: "¡Ticket Generado!",
      text: "El ticket se ha descargado exitosamente.",
      icon: "success",
      confirmButtonText: "Aceptar",
      confirmButtonColor: "#10b981",
    });
  } catch (err) {
    console.error("Error generando ticket:", err);
    Swal.fire({
      title: "Error",
      text: "Hubo un problema al generar el ticket. Por favor intenta de nuevo.",
      icon: "error",
      confirmButtonText: "Cerrar",
      confirmButtonColor: "#ef4444",
    });
  }
}
</script>

<template>
  <main class="sales-wrap">
    <header class="top">
      <h1 class="title">Ventas</h1>
      <p class="subtitle">Gestión y detalles de todas las ventas</p>
    </header>

    <!-- Statistics Cards -->
    <section class="stats-grid">
      <div class="stat-card today">
        <span class="material-symbols-rounded stat-icon">today</span>
        <div class="stat-content">
          <span class="stat-label">Ventas Hoy</span>
          <span class="stat-value">{{ Math.round(animToday) }}</span>
          <span class="stat-sub">{{ money(animTodayAmount) }}</span>
        </div>
      </div>
      <div class="stat-card month">
        <span class="material-symbols-rounded stat-icon">calendar_month</span>
        <div class="stat-content">
          <span class="stat-label">Ventas Este Mes</span>
          <span class="stat-value">{{ Math.round(animMonth) }}</span>
          <span class="stat-sub">{{ money(animMonthAmount) }}</span>
        </div>
      </div>
      <div class="stat-card total">
        <span class="material-symbols-rounded stat-icon">payments</span>
        <div class="stat-content">
          <span class="stat-label">Total General</span>
          <span class="stat-value">{{ Math.round(animTotalCount) }}</span>
          <span class="stat-sub">{{ money(animAllAmount) }}</span>
        </div>
      </div>
      <div class="stat-card avg">
        <span class="material-symbols-rounded stat-icon">analytics</span>
        <div class="stat-content">
          <span class="stat-label">Promedio por Venta</span>
          <span class="stat-value">{{ money(animAvg) }}</span>
          <span class="stat-sub">Todos los tiempos</span>
        </div>
      </div>
    </section>

    <!-- Filters -->
    <section class="filters">
      <div class="filter-field">
        <label class="filter-label">
          <span class="material-symbols-rounded">search</span>
          Buscar
        </label>
        <div class="search-wrap" ref="searchWrapRef">
          <input type="text" v-model="search" class="filter-input" placeholder="Cliente, doc, método, producto..."
            @focus="openSuggestions" @input="suggestOpen = true" @keydown.escape="suggestOpen = false"
            @blur="setTimeout(() => suggestOpen = false, 120)" />
          <div v-if="suggestOpen" class="suggest-backdrop" @click="suggestOpen = false"></div>
          <div v-if="suggestOpen" class="suggest-box">
            <div v-if="suggestLoading" class="suggest-loading">Buscando…</div>
            <ul v-else class="suggest-list">
              <li v-for="p in productSuggestions" :key="p.id" class="suggest-item"
                @mousedown.prevent="selectSuggestion(p)">
                <img v-if="p.image_base64" :src="`data:${p.image_mime};base64,${p.image_base64}`" alt=""
                  class="suggest-thumb" />
                <div class="suggest-info">
                  <div class="suggest-name">{{ p.name }}</div>
                  <div class="suggest-sku" v-if="p.sku">{{ p.sku }}</div>
                </div>
              </li>
              <li v-if="!productSuggestions.length && !suggestLoading" class="suggest-empty">
                Sin resultados
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="filter-field">
        <label class="filter-label">
          <span class="material-symbols-rounded">event</span>
          Desde
        </label>
        <input type="date" v-model="dateFrom" class="filter-input" />
      </div>
      <div class="filter-field">
        <label class="filter-label">
          <span class="material-symbols-rounded">event</span>
          Hasta
        </label>
        <input type="date" v-model="dateTo" class="filter-input" />
      </div>
      <div class="filter-field">
        <label class="filter-label">
          <span class="material-symbols-rounded">credit_card</span>
          Método de Pago
        </label>
        <select v-model="paymentFilter" class="filter-input">
          <option value="">Todos</option>
          <option v-for="m in paymentMethods" :key="m" :value="m">
            {{ m }}
          </option>
        </select>
      </div>
      <button class="btn-clear" @click="clearFilters" title="Limpiar filtros">
        <span class="material-symbols-rounded">filter_alt_off</span>
        Limpiar
      </button>
    </section>

    <!-- Sales Table -->
    <section class="table-container">
      <p v-if="loading" class="muted">Cargando ventas...</p>
      <div v-else-if="filteredSales.length === 0" class="empty-state">
        <span class="material-symbols-rounded empty-icon">receipt_long</span>
        <p class="empty-text">No se encontraron ventas</p>
      </div>
      <div v-else class="table-scroll">
        <table class="sales-table">
          <thead>
            <tr>
              <th>ID / Doc</th>
              <th>Imagen</th>
              <th>Producto</th>
              <th>Cant.</th>
              <th>Fecha</th>
              <th>Cliente</th>
              <th>Método Pago</th>
              <th>Subtotal</th>
              <th>Descuentos</th>
              <th>Impuestos</th>
              <th>Total</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sale in paginatedSales" :key="sale.id" class="sale-row">
              <td>
                <div class="cell-doc">
                  <span class="doc-id">#{{ sale.id }}</span>
                  <span v-if="sale.doc_number" class="doc-num">{{
                    sale.doc_number
                    }}</span>
                </div>
              </td>
              <td>
                <div class="product-image-cell">
                  <img v-if="sale.product_image" :src="`data:${sale.product_image_mime};base64,${sale.product_image}`"
                    alt="Producto" class="product-image" />
                  <span v-else class="no-image">Sin imagen</span>
                </div>
              </td>
              <td>
                <div class="item-name">{{ sale.product_name || "—" }}</div>
              </td>
              <td>{{ quantityForSale(sale) ?? "-" }}</td>
              <td>{{ formatDate(sale.created_at) }}</td>
              <td>{{ sale.customer_name }}</td>
              <td>
                <span class="payment-badge" :class="sale.payment_method?.toLowerCase()">
                  <span class="material-symbols-rounded badge-icon">
                    {{
                      sale.payment_method?.toLowerCase() === "cash"
                        ? "payments"
                        : "credit_card"
                    }}
                  </span>
                  {{ sale.payment_method || "N/A" }}
                </span>
              </td>
              <td>{{ money(sale.subtotal) }}</td>
              <td class="discount-cell">{{ discountPercentForSale(sale) }}%</td>
              <td>{{ taxPercentForSale(sale) }}%</td>
              <td class="total-cell">{{ money(sale.total) }}</td>
              <td>
                <button class="btn-detail" @click="viewDetail(sale)" title="Ver detalles">
                  <span class="material-symbols-rounded">visibility</span>
                  Ver
                </button>
                <button class="btn-print" @click="printTicket(sale)" title="Imprimir ticket">
                  <span class="material-symbols-rounded">receipt_long</span>
                  Ticket
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="filteredSales.length > itemsPerPage" class="pagination">
        <div class="pagination-info">
          Mostrando {{ (currentPage - 1) * itemsPerPage + 1 }}-{{
            Math.min(currentPage * itemsPerPage, filteredSales.length)
          }}
          de {{ filteredSales.length }}
        </div>
        <div class="pagination-pages">
          <button class="pagination-btn" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
            <span class="material-symbols-rounded">chevron_left</span>
          </button>
          <template v-for="(page, idx) in paginationRange" :key="idx">
            <span v-if="page === '...'" class="pagination-ellipsis">...</span>
            <button v-else class="pagination-btn" :class="{ active: page === currentPage }" @click="goToPage(page)">
              {{ page }}
            </button>
          </template>
          <button class="pagination-btn" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
            <span class="material-symbols-rounded">chevron_right</span>
          </button>
        </div>
      </div>
    </section>

    <!-- Detail Modal -->
    <div v-if="showDetailModal" class="modal-overlay" @click.self="closeDetailModal">
      <div class="modal">
        <header class="modal-header">
          <h2 class="modal-title">
            <span class="material-symbols-rounded modal-icon">receipt</span>
            <span class="modal-title-text">Detalle de Venta</span>
            <span class="id-chip">#{{ selectedSale?.id }}</span>
          </h2>
          <button class="btn-close" @click="closeDetailModal" title="Cerrar">
            <span class="material-symbols-rounded">close</span>
          </button>
        </header>

        <div v-if="loadingDetail" class="modal-loading">
          <p class="muted">Cargando detalles...</p>
        </div>

        <div v-else-if="selectedSale" class="modal-body">
          <!-- Sale Info -->
          <section class="modal-section">
            <h3 class="section-title">Información de Venta</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Documento:</span>
                <span class="info-value">{{
                  selectedSale.doc_number || "Sin documento"
                  }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Fecha:</span>
                <span class="info-value">{{
                  formatDate(selectedSale.created_at)
                  }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Método de Pago:</span>
                <span class="info-value">{{
                  selectedSale.payment_method || "N/A"
                  }}</span>
              </div>
              <div class="info-item" v-if="selectedSale.notes">
                <span class="info-label">Notas:</span>
                <span class="info-value">{{ prettyNotes(selectedSale.notes, selectedSale.payment_method) }}</span>
              </div>
            </div>
          </section>

          <!-- Customer Info -->
          <section class="modal-section" v-if="selectedSale.customer">
            <h3 class="section-title">Cliente</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Nombre:</span>
                <span class="info-value">{{ selectedSale.customer.name }}</span>
              </div>
              <div class="info-item" v-if="selectedSale.customer.phone">
                <span class="info-label">Teléfono:</span>
                <span class="info-value">{{
                  selectedSale.customer.phone
                  }}</span>
              </div>
              <div class="info-item" v-if="selectedSale.customer.email">
                <span class="info-label">Email:</span>
                <span class="info-value">{{
                  selectedSale.customer.email
                  }}</span>
              </div>
              <div class="info-item" v-if="selectedSale.customer.address">
                <span class="info-label">Dirección:</span>
                <span class="info-value">{{
                  selectedSale.customer.address
                  }}</span>
              </div>
            </div>
          </section>

          <!-- Items -->
          <section class="modal-section">
            <h3 class="section-title">Productos</h3>
            <div class="items-table-scroll">
              <table class="items-table">
                <thead>
                  <tr>
                    <th>Imagen</th>
                    <th>Producto</th>
                    <th>Cant.</th>
                    <th>Precio Unit.</th>
                    <th>Desc.</th>
                    <th>IVA %</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in saleItems" :key="item.id">
                    <td>
                      <div class="product-image-cell">
                        <img v-if="item.product_image"
                          :src="`data:${item.product_image_mime};base64,${item.product_image}`" :alt="item.product_name"
                          class="product-image" />
                        <span v-else class="no-image">Sin imagen</span>
                      </div>
                    </td>
                    <td>
                      <div class="item-name">{{ item.product_name }}</div>
                      <div v-if="item.product_sku" class="item-sku">
                        SKU: {{ item.product_sku }}
                      </div>
                    </td>
                    <td>{{ item.quantity }}</td>
                    <td>{{ money(item.unit_price) }}</td>
                    <td>{{ displayItemDiscountPercent(item) }}%</td>
                    <td>{{ displayItemTaxRate(item) }}%</td>
                    <td class="item-total">{{ money(item.total) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- Totals -->
          <section class="modal-section totals-section">
            <div class="total-row">
              <span class="total-label">Subtotal:</span>
              <span class="total-value">{{ money(selectedSale.subtotal) }}</span>
            </div>
            <div class="total-row">
              <span class="total-label">Descuentos:</span>
              <span class="total-value discount">{{ overallDiscountPercent() }}%</span>
            </div>
            <div class="total-row">
              <span class="total-label">Impuestos:</span>
              <span class="total-value">{{ overallTaxPercent() }}%</span>
            </div>
            <div class="total-row grand-total">
              <span class="total-label">Total:</span>
              <span class="total-value">{{ money(selectedSale.total) }}</span>
            </div>
          </section>
        </div>
      </div>
    </div>

    <ChatAssistant />
  </main>
</template>

<style scoped>
.sales-wrap {
  display: grid;
  gap: 1.5rem;
  padding: 1.2rem;
  max-width: 1600px;
  margin: 0 auto;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
}

.top {
  display: grid;
  gap: 0.2rem;
}

.title {
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subtitle {
  color: var(--muted);
  font-size: 0.95rem;
}

/* Statistics Cards */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.25rem;
  margin-bottom: 0.5rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  border-radius: 12px;
  background: rgba(20, 24, 30, 0.95);
  border: 1px solid rgba(198, 146, 61, 0.3);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card.today {
  background: #fed7aa;
  border-color: #f59e0b;
}

.stat-card.month {
  background: rgba(223, 192, 94, 0.2);
  border-color: rgba(198, 146, 61, 0.5);
}

.stat-card.total {
  background: #d1fae5;
  border-color: #10b981;
}

.stat-card.avg {
  background: #fce7f3;
  border-color: #d946ef;
}

.stat-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  font-size: 48px;
  color: #DFC05E;
  background: rgba(198, 146, 61, 0.2);
  padding: 1rem;
  border-radius: 14px;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  min-height: 64px;
}

.stat-card.today .stat-icon {
  background: #f59e0b;
  color: #ffffff;
}

.stat-card.month .stat-icon {
  background: rgba(198, 146, 61, 0.4);
  color: #DFC05E;
}

.stat-card.total .stat-icon {
  background: #34d399;
  color: #065f46;
}

.stat-card.avg .stat-icon {
  background: #f9a8d4;
  color: #7c3aed;
}

.stat-card:hover .stat-icon {
  transform: scale(1.05);
}

.stat-content {
  display: grid;
  gap: 0.3rem;
  flex: 1;
}

.stat-label {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 1.875rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1;
  margin-bottom: 0.125rem;
}

.stat-sub {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.stat-card.today .stat-label,
.stat-card.today .stat-value,
.stat-card.today .stat-sub {
  color: rgba(255, 255, 255, 0.9);
}

.stat-card.month .stat-label,
.stat-card.month .stat-value,
.stat-card.month .stat-sub {
  color: rgba(255, 255, 255, 0.9);
}

.stat-card.total .stat-label,
.stat-card.total .stat-value,
.stat-card.total .stat-sub {
  color: rgba(255, 255, 255, 0.9);
}

.stat-card.avg .stat-label,
.stat-card.avg .stat-value,
.stat-card.avg .stat-sub {
  color: rgba(255, 255, 255, 0.9);
}

/* Filters */
.filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  border-radius: 12px;
  background: rgba(20, 24, 30, 0.95);
  border: 1px solid rgba(198, 146, 61, 0.3);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.filter-field {
  display: grid;
  gap: 0.5rem;
}

.search-wrap {
  position: relative;
}

.suggest-backdrop {
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: 900;
}

.suggest-box {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  margin-top: 6px;
}

.suggest-loading {
  padding: 0.6rem 0.8rem;
  color: var(--muted);
}

.suggest-list {
  list-style: none;
  margin: 0;
  padding: 0.4rem 0;
  max-height: 300px;
  overflow-y: auto;
}

.suggest-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.8rem;
  cursor: pointer;
}

.suggest-item:hover {
  background: #f8fafc;
}

.suggest-thumb {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
}

.suggest-info {
  display: grid;
}

.suggest-name {
  font-weight: 600;
}

.suggest-sku {
  font-size: 0.8rem;
  color: var(--muted);
}

.suggest-empty {
  padding: 0.6rem 0.8rem;
  color: var(--muted);
}

.filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-label .material-symbols-rounded {
  font-size: 18px;
}

.filter-input {
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(198, 146, 61, 0.3);
  background: rgba(20, 24, 30, 0.8);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 400;
  transition: all 0.2s ease;
}

.filter-input:hover {
  border-color: #9ca3af;
}

.filter-input:focus {
  outline: none;
  border-color: rgba(198, 146, 61, 0.6);
  box-shadow: 0 0 0 3px rgba(198, 146, 61, 0.2);
}

.btn-clear {
  align-self: end;
  padding: 0.75rem 1.5rem;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #f43f5e, #e11d48);
  color: white;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(244, 63, 94, 0.2);
}

.btn-clear:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(244, 63, 94, 0.3);
}

.btn-clear:active {
  transform: translateY(0);
}

/* Table */
.table-container {
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.table-scroll {
  overflow-x: auto;
}

.table-header-info {
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg,
      rgba(124, 92, 255, 0.05),
      rgba(37, 99, 235, 0.05));
  border-bottom: 1px solid var(--border);
  color: var(--muted);
  font-weight: 600;
  font-size: 0.9rem;
}

/* Pagination */
.pagination {
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  border-top: 1px solid var(--border);
  background: linear-gradient(135deg,
      rgba(124, 92, 255, 0.02),
      rgba(37, 99, 235, 0.02));
}

.pagination-info {
  color: var(--muted);
  font-size: 0.875rem;
  font-weight: 600;
}

.pagination-pages {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.pagination-btn {
  padding: 0.5rem 0.9rem;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  text-align: center;
}

.pagination-btn:hover:not(:disabled) {
  background: rgba(124, 92, 255, 0.1);
  border-color: var(--accent);
  color: var(--accent);
}

.pagination-btn.active {
  background: linear-gradient(135deg, #7c5cff, #2563eb);
  color: white;
  border-color: transparent;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-ellipsis {
  padding: 0.5rem;
  color: var(--muted);
}

.sales-table {
  width: 100%;
  border-collapse: collapse;
}

.sales-table thead {
  background: linear-gradient(135deg,
      rgba(124, 92, 255, 0.12),
      rgba(37, 99, 235, 0.12));
}

.sales-table th {
  padding: 1rem;
  text-align: center;
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--text);
  border-bottom: 2px solid rgba(124, 92, 255, 0.2);
}

.sales-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--border);
  font-size: 0.95rem;
  color: var(--text);
  text-align: center;
  vertical-align: middle;
}

.sale-row {
  transition: background 0.2s ease;
}

.sale-row:hover {
  background: rgba(124, 92, 255, 0.05);
}

.cell-doc {
  display: grid;
  gap: 0.2rem;
  place-items: center;
}

.doc-id {
  font-weight: 700;
  color: #7c5cff;
}

.doc-num {
  font-size: 0.85rem;
  color: var(--muted);
}

.payment-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  background: #d1fae5;
  color: #065f46;
  font-size: 0.8125rem;
  font-weight: 600;
}

.payment-badge.card {
  background: #dbeafe;
  color: #1e40af;
}

.badge-icon {
  font-size: 16px;
}

.discount-cell {
  color: #ef4444;
  font-weight: 600;
}

.total-cell {
  font-weight: 700;
  font-size: 1.05rem;
  color: #10b981;
}

/* Product images in table */
.sales-table .product-image-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.sales-table .product-image {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.sales-table .no-image {
  font-size: 0.75rem;
  color: var(--muted);
  font-style: italic;
}

.btn-detail {
  padding: 0.5rem 0.9rem;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #7c5cff, #2563eb);
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.btn-detail:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(124, 92, 255, 0.3);
}

.empty-state {
  padding: 3rem;
  text-align: center;
  color: var(--muted);
}

.empty-icon {
  font-size: 64px;
  opacity: 0.3;
}

.empty-text {
  margin-top: 1rem;
  font-size: 1.1rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: radial-gradient(1200px 600px at 20% 20%,
      rgba(124, 92, 255, 0.14),
      rgba(0, 194, 168, 0.12)),
    rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(10px) saturate(120%);
  display: grid;
  place-items: center;
  z-index: 99999;
  padding: 1rem;
  overflow-y: auto;
}

.modal {
  background: var(--panel-bg);
  border-radius: 20px;
  max-width: 940px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.28);
  border: 1px solid var(--panel-border);
  animation: modalIn 220ms ease-out;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--panel-border);
  position: sticky;
  top: 0;
  background: var(--exotic-gradient-soft);
  z-index: 10;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: var(--text);
}

.modal-title-text {
  letter-spacing: 0.2px;
}

.modal-icon {
  font-size: 24px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid var(--panel-border);
  color: var(--icon-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
}

.id-chip {
  padding: 0.3rem 0.6rem;
  border-radius: 999px;
  background: rgba(124, 92, 255, 0.15);
  color: var(--accent);
  font-weight: 700;
  border: 1px solid rgba(124, 92, 255, 0.35);
  font-size: 0.85rem;
}

.btn-close {
  padding: 0.5rem;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.btn-close:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.modal-loading {
  padding: 3rem;
  text-align: center;
}

.modal-body {
  padding: 1.25rem 1.5rem;
  display: grid;
  gap: 1.25rem;
}

.modal-section {
  display: grid;
  gap: 1rem;
  background: var(--surface-2);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  padding: 1rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 800;
  color: var(--text);
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(124, 92, 255, 0.2);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-item {
  display: grid;
  gap: 0.3rem;
}

.info-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--muted);
}

.info-value {
  font-size: 0.95rem;
  color: var(--text);
}

.items-table-scroll {
  overflow: auto;
  max-height: 42vh;
  border: 1px solid var(--panel-border);
  border-radius: 12px;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
}

.items-table th {
  padding: 0.8rem;
  text-align: left;
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--muted);
  border-bottom: 2px solid var(--panel-border);
  position: sticky;
  top: 0;
  background: var(--panel-bg);
}

.items-table td {
  padding: 0.8rem;
  border-bottom: 1px solid var(--panel-border);
  font-size: 0.9rem;
}

.items-table tbody tr:nth-child(even) {
  background: rgba(124, 92, 255, 0.06);
}

.items-table td:nth-child(3),
.items-table td:nth-child(4),
.items-table td:nth-child(5),
.items-table td:nth-child(6),
.items-table td:nth-child(7) {
  text-align: right;
}

.items-table th:nth-child(3),
.items-table th:nth-child(4),
.items-table th:nth-child(5),
.items-table th:nth-child(6),
.items-table th:nth-child(7) {
  text-align: right;
}

.item-name {
  font-weight: 600;
  color: var(--text);
}

.item-sku {
  font-size: 0.8rem;
  color: var(--muted);
  margin-top: 0.2rem;
}

.product-image-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-image {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.no-image {
  font-size: 0.75rem;
  color: var(--muted);
  font-style: italic;
}

.item-total {
  font-weight: 700;
  color: #10b981;
}

.totals-section {
  background: linear-gradient(135deg,
      rgba(124, 92, 255, 0.06),
      rgba(0, 194, 168, 0.06));
  padding: 1.2rem;
  border-radius: 16px;
  border: 1px solid var(--panel-border);
}

.total-row {
  display: flex;
  justify-content: space-between;
  padding: 0.6rem 0;
  border-bottom: 1px solid var(--border);
}

.total-row:last-child {
  border-bottom: none;
}

.total-label {
  font-weight: 600;
  color: var(--muted);
}

.mini-badge {
  margin-left: 0.5rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  border: 1px solid var(--panel-border);
  background: rgba(124, 92, 255, 0.1);
  color: var(--accent);
  font-weight: 700;
  font-size: 0.8rem;
}

.total-value {
  font-weight: 700;
  color: var(--text);
}

.total-value.discount {
  color: #ef4444;
}

.grand-total {
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 2px solid rgba(124, 92, 255, 0.3);
}

.grand-total .total-label {
  font-size: 1.1rem;
  color: var(--text);
}

.grand-total .total-value {
  font-size: 1.3rem;
  color: #10b981;
}

.muted {
  color: var(--muted);
}

/* Responsive */
@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .filters {
    grid-template-columns: 1fr;
  }

  .table-scroll {
    font-size: 0.85rem;
  }

  .sales-table th,
  .sales-table td {
    padding: 0.7rem 0.5rem;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }
}

@keyframes modalIn {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Buttons */
.btn-detail,
.btn-print {
  width: 80px;
  /* equal width */
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  transition: background 0.2s;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}

.btn-detail {
  background: #2563eb;
  color: white;
}

.btn-detail:hover {
  background: #1e40af;
}

.btn-print {
  background: #10b981;
  color: white;
}

.btn-print:hover {
  background: #059669;
}

/* Dark Mode Support */
.sales-wrap {
  color-scheme: light dark;
}

@media (prefers-color-scheme: dark) {
  .sales-wrap {
    color-scheme: dark;
  }

  /* Stat Cards - Colores oscuros para mejor contraste */
  .stat-card.today {
    background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
    border-color: #ea580c;
  }

  .stat-card.month {
    background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
    border-color: #1e40af;
  }

  .stat-card.total {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-color: #059669;
  }

  .stat-card.avg {
    background: linear-gradient(135deg, #d946ef 0%, #c026d3 100%);
    border-color: #c026d3;
  }

  /* Text color for dark stat cards */
  .stat-card.today .stat-label,
  .stat-card.today .stat-value,
  .stat-card.today .stat-sub,
  .stat-card.month .stat-label,
  .stat-card.month .stat-value,
  .stat-card.month .stat-sub,
  .stat-card.total .stat-label,
  .stat-card.total .stat-value,
  .stat-card.total .stat-sub,
  .stat-card.avg .stat-label,
  .stat-card.avg .stat-value,
  .stat-card.avg .stat-sub {
    color: #ffffff;
  }

  /* Icon background for dark cards */
  .stat-card.today .stat-icon {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }

  .stat-card.month .stat-icon {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }

  .stat-card.total .stat-icon {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }

  .stat-card.avg .stat-icon {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }

  /* Filters section */
  .filters {
    background: #1e293b !important;
    border-color: #334155 !important;
  }

  .filter-input {
    background: #0f172a !important;
    border-color: #334155 !important;
    color: #e2e8f0 !important;
  }

  .filter-input:hover {
    border-color: #475569 !important;
  }

  .filter-input:focus {
    border-color: #6366f1 !important;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2) !important;
  }

  .filter-label {
    color: #94a3b8 !important;
  }

  /* Table */
  .table-container {
    background: #1e293b !important;
    border-color: #334155 !important;
  }

  .sales-table {
    background: #1e293b !important;
  }

  .sales-table thead {
    background: #0f172a !important;
  }

  .sales-table th {
    color: #e2e8f0 !important;
    border-color: #334155 !important;
  }

  .sales-table td {
    color: #cbd5e1 !important;
    border-color: #334155 !important;
  }

  .sale-row {
    background: #1e293b !important;
  }

  .sale-row:hover {
    background: #334155 !important;
  }

  /* Suggestions box */
  .suggest-box {
    background: #1e293b;
    border-color: #334155;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.5);
  }

  .suggest-item:hover {
    background: #334155;
  }

  .suggest-loading,
  .suggest-empty {
    color: #94a3b8;
  }

  .suggest-name {
    color: #e2e8f0;
  }

  .suggest-sku {
    color: #94a3b8;
  }

  /* Modal */
  .modal {
    background: #1e293b;
    border-color: #334155;
  }

  .modal-header {
    background: #0f172a;
    border-color: #334155;
  }

  .modal-title {
    color: #e2e8f0;
  }

  .section-title {
    color: #e2e8f0;
  }

  .info-label {
    color: #94a3b8;
  }

  .info-value {
    color: #cbd5e1;
  }

  .items-table {
    background: #0f172a;
  }

  .items-table th {
    color: #e2e8f0;
    background: #0f172a;
    border-color: #334155;
  }

  .items-table td {
    color: #cbd5e1;
    border-color: #334155;
  }

  .modal-overlay {
    background: rgba(0, 0, 0, 0.7);
  }

  .pagination {
    background: #0f172a;
    border-color: #334155;
  }

  .pagination-info {
    color: #94a3b8;
  }

  .pagination-btn {
    background: #1e293b;
    border-color: #334155;
    color: #e2e8f0;
  }

  .pagination-btn:hover:not(:disabled) {
    background: #334155;
    border-color: #6366f1;
    color: #a5b4fc;
  }

  .pagination-ellipsis {
    color: #64748b;
  }

  /* Empty state */
  .empty-state {
    color: #94a3b8;
  }

  .empty-icon {
    color: #64748b;
  }

  /* Product images */
  .no-image {
    background: #334155;
    color: #94a3b8;
  }
}

/* Light mode */
@media (prefers-color-scheme: light) {
  .sales-wrap {
    color-scheme: light;
  }
}

:root[data-theme="dark"] .sales-wrap {
  color-scheme: dark;
}

/* Force Light Mode - Overrides system dark mode */
:root[data-theme="light"] {
  .sales-wrap {
    color-scheme: light;
  }

  /* Stat Cards */
  .stat-card.today {
    background: #fed7aa !important;
    border-color: #f59e0b !important;
  }

  .stat-card.today .stat-label,
  .stat-card.today .stat-value,
  .stat-card.today .stat-sub {
    color: #111827 !important;
  }

  .stat-card.today .stat-icon {
    background: #f59e0b !important;
    color: #ffffff !important;
  }

  .stat-card.month {
    background: #dbeafe !important;
    border-color: #2563eb !important;
  }

  .stat-card.month .stat-label,
  .stat-card.month .stat-value,
  .stat-card.month .stat-sub {
    color: #111827 !important;
  }

  .stat-card.month .stat-icon {
    background: #93c5fd !important;
    color: #1e40af !important;
  }

  .stat-card.total {
    background: #d1fae5 !important;
    border-color: #10b981 !important;
  }

  .stat-card.total .stat-label,
  .stat-card.total .stat-value,
  .stat-card.total .stat-sub {
    color: #111827 !important;
  }

  .stat-card.total .stat-icon {
    background: #34d399 !important;
    color: #065f46 !important;
  }

  .stat-card.avg {
    background: #fce7f3 !important;
    border-color: #d946ef !important;
  }

  .stat-card.avg .stat-label,
  .stat-card.avg .stat-value,
  .stat-card.avg .stat-sub {
    color: #111827 !important;
  }

  .stat-card.avg .stat-icon {
    background: #f9a8d4 !important;
    color: #7c3aed !important;
  }

  /* Filters */
  .filters {
    background: #ffffff !important;
    border-color: #e5e7eb !important;
  }

  .filter-input {
    background: #ffffff !important;
    border-color: #d1d5db !important;
    color: #111827 !important;
    color-scheme: light !important;
  }

  .filter-input option {
    background-color: #ffffff !important;
    color: #111827 !important;
  }

  .filter-label {
    color: #6b7280 !important;
  }

  /* Table */
  .table-container {
    background: #ffffff !important;
    border-color: #e5e7eb !important;
  }

  .sales-table {
    background: transparent !important;
  }

  .sales-table thead {
    background: linear-gradient(135deg, rgba(124, 92, 255, 0.12), rgba(37, 99, 235, 0.12)) !important;
  }

  .sales-table th {
    color: #111827 !important;
    border-color: rgba(124, 92, 255, 0.2) !important;
    background: transparent !important;
  }

  .sales-table td {
    color: #111827 !important;
    border-color: #e5e7eb !important;
  }

  .sale-row {
    background: transparent !important;
  }

  .sale-row:hover {
    background: rgba(124, 92, 255, 0.05) !important;
  }

  /* Pagination */
  .pagination {
    background: linear-gradient(135deg, rgba(124, 92, 255, 0.02), rgba(37, 99, 235, 0.02)) !important;
    border-color: #e5e7eb !important;
  }

  .pagination-btn {
    background: #ffffff !important;
    border-color: #e5e7eb !important;
    color: #111827 !important;
  }

  .pagination-info {
    color: #6b7280 !important;
  }

  /* Modal */
  .modal {
    background: #ffffff !important;
    border-color: #e5e7eb !important;
  }

  .modal-header {
    background: #ffffff !important;
    border-color: #e5e7eb !important;
  }

  .modal-title,
  .section-title {
    color: #111827 !important;
  }

  .info-label {
    color: #6b7280 !important;
  }

  .info-value {
    color: #111827 !important;
  }

  .items-table {
    background: transparent !important;
  }

  .items-table th {
    background: #ffffff !important;
    color: #6b7280 !important;
    border-color: #e5e7eb !important;
  }

  .items-table td {
    color: #111827 !important;
    border-color: #e5e7eb !important;
  }

  .no-image {
    background: #f3f4f6 !important;
    color: #9ca3af !important;
  }

  .suggest-box {
    background: #ffffff !important;
    border-color: #e5e7eb !important;
  }

  .suggest-item:hover {
    background: #f8fafc !important;
  }

  .suggest-name {
    color: #111827 !important;
  }

  .suggest-sku {
    color: #6b7280 !important;
  }

  .empty-state {
    color: #6b7280 !important;
  }

  .empty-icon {
    color: #d1d5db !important;
  }
}

/* Dark Mode Support */
.sales-wrap {
  color-scheme: light dark;
}

/* Dark mode via system preference OR manual toggle */
@media (prefers-color-scheme: dark) {
  .sales-wrap {
    color-scheme: dark;
  }

  /* Stat Cards - Colores oscuros para mejor contraste */
  .stat-card.today {
    background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
    border-color: #ea580c;
  }

  .stat-card.month {
    background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
    border-color: #1e40af;
  }

  .stat-card.total {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-color: #059669;
  }

  .stat-card.avg {
    background: linear-gradient(135deg, #d946ef 0%, #c026d3 100%);
    border-color: #c026d3;
  }

  /* Text color for dark stat cards */
  .stat-card.today .stat-label,
  .stat-card.today .stat-value,
  .stat-card.today .stat-sub,
  .stat-card.month .stat-label,
  .stat-card.month .stat-value,
  .stat-card.month .stat-sub,
  .stat-card.total .stat-label,
  .stat-card.total .stat-value,
  .stat-card.total .stat-sub,
  .stat-card.avg .stat-label,
  .stat-card.avg .stat-value,
  .stat-card.avg .stat-sub {
    color: #ffffff;
  }

  /* Icon background for dark cards */
  .stat-card.today .stat-icon {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }

  .stat-card.month .stat-icon {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }

  .stat-card.total .stat-icon {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }

  .stat-card.avg .stat-icon {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }

  /* Filters section */
  .filters {
    background: #1e293b !important;
    border-color: #334155 !important;
  }

  .filter-input {
    background: #0f172a !important;
    border-color: #334155 !important;
    color: #e2e8f0 !important;
  }

  .filter-input:hover {
    border-color: #475569 !important;
  }

  .filter-input:focus {
    border-color: #6366f1 !important;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2) !important;
  }

  .filter-label {
    color: #94a3b8 !important;
  }

  /* Table */
  .table-container {
    background: #1e293b !important;
    border-color: #334155 !important;
  }

  .sales-table {
    background: #1e293b !important;
  }

  .sales-table thead {
    background: #0f172a !important;
  }

  .sales-table th {
    color: #e2e8f0 !important;
    border-color: #334155 !important;
  }

  .sales-table td {
    color: #cbd5e1 !important;
    border-color: #334155 !important;
  }

  .sale-row {
    background: #1e293b !important;
  }

  .sale-row:hover {
    background: #334155 !important;
  }

  /* Suggestions box */
  .suggest-box {
    background: #1e293b;
    border-color: #334155;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.5);
  }

  .suggest-item:hover {
    background: #334155;
  }

  .suggest-loading,
  .suggest-empty {
    color: #94a3b8;
  }

  .suggest-name {
    color: #e2e8f0;
  }

  .suggest-sku {
    color: #94a3b8;
  }

  /* Modal */
  .modal {
    background: #1e293b;
    border-color: #334155;
  }

  .modal-header {
    background: #0f172a;
    border-color: #334155;
  }

  .modal-title {
    color: #e2e8f0;
  }

  .section-title {
    color: #e2e8f0;
  }

  .info-label {
    color: #94a3b8;
  }

  .info-value {
    color: #cbd5e1;
  }

  .items-table {
    background: #0f172a;
  }

  .items-table th {
    color: #e2e8f0;
    background: #0f172a;
    border-color: #334155;
  }

  .items-table td {
    color: #cbd5e1;
    border-color: #334155;
  }

  .modal-overlay {
    background: rgba(0, 0, 0, 0.7);
  }

  .pagination {
    background: #0f172a;
    border-color: #334155;
  }

  .pagination-info {
    color: #94a3b8;
  }

  .pagination-btn {
    background: #1e293b;
    border-color: #334155;
    color: #e2e8f0;
  }

  .pagination-btn:hover:not(:disabled) {
    background: #334155;
    border-color: #6366f1;
    color: #a5b4fc;
  }

  .pagination-ellipsis {
    color: #64748b;
  }

  /* Empty state */
  .empty-state {
    color: #94a3b8;
  }

  .empty-icon {
    color: #64748b;
  }

  /* Product images */
  .no-image {
    background: #334155;
    color: #94a3b8;
  }
}

/* Dark mode via manual toggle (data-theme attribute) */
:root[data-theme="dark"] {

  /* Stat Cards */
  .stat-card.today {
    background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
    border-color: #ea580c;
  }

  .stat-card.month {
    background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
    border-color: #1e40af;
  }

  .stat-card.total {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-color: #059669;
  }

  .stat-card.avg {
    background: linear-gradient(135deg, #d946ef 0%, #c026d3 100%);
    border-color: #c026d3;
  }

  .stat-card.today .stat-label,
  .stat-card.today .stat-value,
  .stat-card.today .stat-sub,
  .stat-card.month .stat-label,
  .stat-card.month .stat-value,
  .stat-card.month .stat-sub,
  .stat-card.total .stat-label,
  .stat-card.total .stat-value,
  .stat-card.total .stat-sub,
  .stat-card.avg .stat-label,
  .stat-card.avg .stat-value,
  .stat-card.avg .stat-sub {
    color: #ffffff;
  }

  .stat-card.today .stat-icon,
  .stat-card.month .stat-icon,
  .stat-card.total .stat-icon,
  .stat-card.avg .stat-icon {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }

  /* Filters */
  .filters {
    background: #1e293b !important;
    border-color: #334155 !important;
  }

  .filter-input {
    background: #0f172a !important;
    border-color: #334155 !important;
    color: #e2e8f0 !important;
  }

  .filter-input:hover {
    border-color: #475569 !important;
  }

  .filter-input:focus {
    border-color: #6366f1 !important;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2) !important;
  }

  .filter-label {
    color: #94a3b8 !important;
  }

  /* Table */
  .table-container {
    background: #1e293b !important;
    border-color: #334155 !important;
  }

  .sales-table {
    background: #1e293b !important;
  }

  .sales-table thead {
    background: #0f172a !important;
  }

  .sales-table th {
    color: #e2e8f0 !important;
    border-color: #334155 !important;
  }

  .sales-table td {
    color: #cbd5e1 !important;
    border-color: #334155 !important;
  }

  .sale-row {
    background: #1e293b !important;
  }

  .sale-row:hover {
    background: #334155 !important;
  }

  .suggest-box {
    background: #1e293b;
    border-color: #334155;
  }

  .suggest-item:hover {
    background: #334155;
  }

  .suggest-loading,
  .suggest-empty {
    color: #94a3b8;
  }

  .suggest-name {
    color: #e2e8f0;
  }

  .suggest-sku {
    color: #94a3b8;
  }

  .modal {
    background: #1e293b;
    border-color: #334155;
  }

  .modal-header {
    background: #0f172a;
    border-color: #334155;
  }

  .modal-title,
  .section-title {
    color: #e2e8f0;
  }

  .info-label {
    color: #94a3b8;
  }

  .info-value {
    color: #cbd5e1;
  }

  .items-table {
    background: #0f172a;
  }

  .items-table th {
    color: #e2e8f0;
    background: #0f172a;
    border-color: #334155;
  }

  .items-table td {
    color: #cbd5e1;
    border-color: #334155;
  }

  .modal-overlay {
    background: rgba(0, 0, 0, 0.7);
  }

  .pagination {
    background: #0f172a;
    border-color: #334155;
  }

  .pagination-info {
    color: #94a3b8;
  }

  .pagination-btn {
    background: #1e293b;
    border-color: #334155;
    color: #e2e8f0;
  }

  .pagination-btn:hover:not(:disabled) {
    background: #334155;
    border-color: #6366f1;
    color: #a5b4fc;
  }

  .pagination-ellipsis {
    color: #64748b;
  }

  .empty-state {
    color: #94a3b8;
  }

  .empty-icon {
    color: #64748b;
  }

  .no-image {
    background: #334155;
    color: #94a3b8;
  }
}

/* Light mode */
@media (prefers-color-scheme: light) {
  .sales-wrap {
    color-scheme: light;
  }
}

:root[data-theme="light"] .sales-wrap {
  color-scheme: light;
}
</style>
